import { useCallback, useMemo } from 'react';

import { buildPropertyPanelTitle, coerceSelfLoopEdgeType, type FlowEdgeLike } from './flowEdgeHelpers';
import { clampLoopEdgeRestartPatch, parseEdgeDataObject, type LoopEdgeRestartData } from './loopEdgeRestartData';

interface FlowEditorState {
  readonly selectedEdgeId?: string | null;
  readonly selectedNode?: { readonly name?: string } | null;
  readonly child_edges?: readonly FlowEdgeLike[];
}

type SetState = (updater: FlowEditorState | ((prev: FlowEditorState) => FlowEditorState)) => void;

interface Options {
  readonly state: FlowEditorState;
  readonly setState: SetState;
  readonly child_edges: readonly FlowEdgeLike[];
  readonly isInsideSubflow: boolean;
  readonly disabled?: boolean;
}

export function useFlowEdgeSelection({ state, setState, child_edges, isInsideSubflow, disabled = false }: Options) {
  const selectedEdge = useMemo(() => {
    if (!state.selectedEdgeId || isInsideSubflow) {
      return null;
    }
    return (child_edges || []).find((edge) => edge.id === state.selectedEdgeId) ?? null;
  }, [state.selectedEdgeId, child_edges, isInsideSubflow]);

  const showPropertyPanel = (!!state.selectedNode || !!selectedEdge) && !isInsideSubflow;

  const selectedNodeTitle = useMemo(() => buildPropertyPanelTitle(selectedEdge, state.selectedNode?.name), [selectedEdge, state.selectedNode?.name]);

  const onEdgeClick = useCallback(
    (_event: unknown, edge: FlowEdgeLike | undefined) => {
      if (isInsideSubflow || disabled) {
        return;
      }
      setState((prev) => ({
        ...prev,
        selectedEdgeId: edge?.id ?? null,
        selectedNode: null,
      }));
    },
    [disabled, isInsideSubflow, setState]
  );

  const handleLoopEdgeRestartChange = useCallback(
    (edgeId: string, next: LoopEdgeRestartData) => {
      const clamped = clampLoopEdgeRestartPatch(next);
      setState((prev) => ({
        ...prev,
        child_edges: (prev.child_edges || []).map((edge) => {
          if (edge.id !== edgeId) {
            return edge;
          }
          return {
            ...edge,
            type: coerceSelfLoopEdgeType(edge),
            data: {
              ...parseEdgeDataObject(edge.data),
              ...clamped,
            },
          };
        }),
      }));
    },
    [setState]
  );

  return {
    selectedEdge,
    showPropertyPanel,
    selectedNodeTitle,
    onEdgeClick,
    handleLoopEdgeRestartChange,
  };
}
